import * as _ from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { IRecipientMedicalDetails } from '@nkr/models/index';
import { ILookupData } from '../lookup-data';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, InputGroup, Label, Row } from 'react-bootstrap';
import { ViewWrapper } from '../styles';
import { IValidation } from './state';
import YesNoUnknown from '../yes-no-unknown';
import MaskedInput from 'react-maskedinput';
import DialysisDate from './dialysis-date';

interface IProps {
    centerId: number,
    recipientId: number,
    showMetric: boolean,
    lookupData: ILookupData,
    data: IRecipientMedicalDetails,
    heightCm: number,
    heightInches: number,
    heightFeet: number,
    weightKgs: number,
    weightLbs: number,
    validation: IValidation,
    isInMatch: boolean,
    onToggleMetric: () => void,
    onChange: (data: IRecipientMedicalDetails) => void,
    onConvertWeight: (lbs: number) => void,
    onConvertHeight: (feet: number, inches: number) => void
}

export const positiveNegativeMap = [
    { value: '', description: 'Please Select' },
    { value: 'DNK', description: 'Don\'t Know' },
    { value: 'POS', description: 'Positive' },
    { value: 'NEG', description: 'Negative' },
];

const Content = (props: IProps) => {

    const [tempHeightFeet, setTempHeightFeet] = useState<number>(0);
    const [tempHeightInches, setTempHeightInches] = useState<number>(0);
    const [tempHeightCmText, setTempHeightCmText] = useState<string>("");



    useEffect(() => {
        const totalInches = props.data.heightInches || 0;
        const { feet, inches } = inchesToFeetInches(totalInches);

        const cm = inchesToCm(totalInches);

        setTempHeightFeet(feet);
        setTempHeightInches(inches);
        if (props.data.height !== undefined) {
            setTempHeightCmText(props.data.height.toString());
        } else {
            setTempHeightCmText("");
        }

    }, [props.data.heightInches, props.data.height, props.data.weightLbs, props.showMetric]);


    const inchesToFeetInches = (totalInches: number) => {
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches % 12;
        return { feet, inches };
    };

    const feetInchesToInches = (feet: number, inches: number) => feet * 12 + inches;

    const inchesToCm = (inches: number) => Math.round(inches * 2.54);


    const handleSelectChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleSelectNumberChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value != null ? parseInt(value) : value;

        props.onChange(data);
    };

    const handleTextChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleClick = (e: FormEvent<HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        data[name] = value;

        props.onChange(data);
    };

    const handleNumberChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;
        let data = _.cloneDeep(props.data);

        if (name == 'height' || name == 'weight') {
            if (value.length <= e.currentTarget.maxLength) {
                const parsed = parseInt(value);
                data[name] = isNaN(parsed) ? undefined : parsed;
            }
        } else {
            const parsed = parseInt(value);
            data[name] = isNaN(parsed) ? "" : Math.abs(parsed);
        }

        props.onChange(data);
    };


    const handleCmChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        setTempHeightCmText(e.currentTarget.value); // just local typing
    };

    const handleCmBlur = () => {
        if (tempHeightCmText.trim() === "") {
            const updated = _.cloneDeep(props.data);
            updated.height = undefined;
            updated.heightInches = undefined;
            props.onChange(updated);
            return;
        }

        const cm = parseInt(tempHeightCmText, 10);
        if (!Number.isNaN(cm)) {
            const totalInches = Math.round(cm / 2.54);
            const updated = _.cloneDeep(props.data);
            updated.height = cm;
            updated.heightInches = totalInches;
            props.onChange(updated);
        }
    };

    const handleBooleanChange = (name: string, value: boolean) => {
        let data = _.cloneDeep(props.data);

        data[name] = value;

        if (name == 'isOnDialysis' && value != true)
            data["dialysisStartDate"] = "";

        props.onChange(data);
    };

    const handleWeightChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        let value = Math.abs(parseInt(e.currentTarget.value));

        props.onConvertWeight(value);
    };

    const handleHeightChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        let feet = tempHeightFeet;
        let inches = tempHeightInches;

        if (name == "heightFeet") feet = parseInt(value);
        if (name == "heightInches") inches = parseInt(value);

        const totalInches = feetInchesToInches(feet, inches);
        const cm = inchesToCm(totalInches);

        setTempHeightFeet(feet);
        setTempHeightInches(inches);

        const newData = _.cloneDeep(props.data);
        newData.height = cm;
        newData.heightInches = totalInches;
        props.onChange(newData);
    };


    const handleDialysisStartDateChange = (newDate: string) => {
        let data = _.cloneDeep(props.data);
        data.dialysisStartDate = newDate;
        props.onChange(data);
    }


    let { data, lookupData } = props;

    if (data == null || lookupData == null) {
        return null;
    }

    let { raceList, antiATiterValueList } = lookupData;

    const heightFeet = _.range(0, 8);
    const heightInches = _.range(0, 12);

    return (
        <>
            <ViewWrapper horizontal>
                {props.isInMatch && <Alert bsStyle='warning' bsSize='sm'>Blood Type can't be changed when the recipient is in a match</Alert>}

                <Row>
                    <Col xs={12} lg={6}>
                        <FormGroup validationState={props.validation.birthDate == false ? 'error' : null}>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Birth Date:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    type='date'
                                    name='birthDate'
                                    value={data.birthDate}
                                    onChange={handleTextChange} />
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col xs={12} lg={6}>
                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>
                                Is Recipient on Dialysis?
                            </Col>
                            <Col xs={12} lg={5}>
                                <div className='form-control-static'>
                                    <YesNoUnknown
                                        name='isOnDialysis'
                                        yes={data.isOnDialysis == true}
                                        no={data.isOnDialysis == false}
                                        unknown={data.isOnDialysis == null}
                                        onChange={handleBooleanChange}
                                    />
                                </div>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} lg={6}>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Blood Type:</Col>
                            <Col xs={12} lg={6}>
                                <div className='form-control-static'>
                                    <label className='radio-inline'>
                                        <input id='BloodTypeA' name='bloodType' type='radio' value='A' className='blood_type_radio' checked={data.bloodType == 'A'} disabled={props.isInMatch} onClick={handleClick} />
                                        <strong>A</strong>
                                    </label>
                                    <label className='radio-inline'>
                                        <input id='BloodTypeB' name='bloodType' type='radio' value='B' className='blood_type_radio' checked={data.bloodType == 'B'} disabled={props.isInMatch} onClick={handleClick} />
                                        <strong>B</strong>
                                    </label>
                                    <label className='radio-inline'>
                                        <input id='BloodTypeO' name='bloodType' type='radio' value='O' className='blood_type_radio' checked={data.bloodType == 'O'} disabled={props.isInMatch} onClick={handleClick} />
                                        <strong>O</strong>
                                    </label>
                                    <label className='radio-inline'>
                                        <input id='BloodTypeAB' name='bloodType' type='radio' value='AB' className='blood_type_radio' checked={data.bloodType == 'AB'} disabled={props.isInMatch} onClick={handleClick} />
                                        <strong>AB</strong>
                                    </label>
                                    <label className='radio-inline'>
                                        <input id='BloodTypeUnknown' name='bloodType' type='radio' value='DNK' className='blood_type_radio' checked={data.bloodType == 'DNK'} disabled={props.isInMatch} onClick={handleClick} />
                                        <strong>Unknown</strong>
                                    </label>
                                </div>
                            </Col>
                        </FormGroup>
                    </Col>
                    <Col xs={12} lg={6}>
                        {data.isOnDialysis == true && (
                            <FormGroup validationState={props.validation.dialysisStartDate == false ? 'error' : null}>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>If 'Yes', Dialysis Start Date:</Col>
                                <Col xs={12} lg={4}>
                                    <DialysisDate
                                        dialysisStartDate={props.data.dialysisStartDate}
                                        onChange={handleDialysisStartDateChange} />
                                </Col>
                            </FormGroup>
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} lg={6}>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>ABO Determined by NKR Lab:</Col>
                            <Col xs={12} lg={6}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div className='form-control-static' style={{ marginRight: 10 }}><Label style={{ fontSize: '100%' }}>{props.data.histogeneticsAbo}</Label></div>
                                    {props.data.histogeneticsAbo?.length > 0 && props.data.bloodType?.toUpperCase() != props.data.histogeneticsAbo.replace('1', '').replace('2', '').toUpperCase() && <div><div style={{ padding: 3, backgroundColor: 'yellow', color: '#333', fontSize: '87%', whiteSpace: 'nowrap' }}>The specified ABO and the ABO determined by the NKR lab do not match...</div></div>}
                                </div>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>

                {(data.bloodType == 'O' || data.bloodType == 'B') &&
                    <Row>
                        <Col xs={6}>
                            <FormGroup>
                                <Col xs={12} lg={5} componentClass={ControlLabel}>Are titers acceptable for non-A1 donors?</Col>
                                <Col xs={12} lg={6}>
                                    <FormControl
                                        componentClass='select'
                                        name='antiATiter'
                                        value={data.antiATiter}
                                        onChange={handleSelectNumberChange}
                                    >
                                        <option value={1}>Unknown Titers</option>
                                        <option value={2}>Unacceptable Titers</option>
                                        <option value={3}>Acceptable Titers</option>
                                        <option value={4}>Candidate not Appropriate</option>
                                        <option value={5}>Candidate not interested</option>
                                        <option value={6}>Unacceptable Titers Center does not perform A2 Transplants</option>
                                    </FormControl>
                                </Col>
                            </FormGroup>
                        </Col>
                        <Col xs={6}>
                            <FormGroup>
                                <Col xs={12} lg={4} componentClass={ControlLabel}>Please enter the titer value:</Col>
                                <Col xs={12} lg={6}>
                                    <FormControl
                                        componentClass='select'
                                        name='antiATiterValue'
                                        value={data.antiATiterValue}
                                        onChange={handleSelectNumberChange}
                                    >
                                        <option value={null}>- Select -</option>
                                        {antiATiterValueList.map(a =>
                                            <option key={'by-' + a.id} value={a.id}>{a.description}</option>
                                        )}
                                    </FormControl>
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                }

                <Row>
                    <Col xs={12} lg={6}>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>
                                Is ABO Accuracy Confirmed?
                            </Col>
                            <Col xs={12} lg={4}>
                                <div className='form-control-static'>
                                    <YesNoUnknown
                                        name='isAboConfirmed'
                                        yes={data.isAboConfirmed == true}
                                        no={data.isAboConfirmed == false}
                                        onChange={handleBooleanChange}
                                    />
                                </div>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} lg={6}>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Height:&nbsp;
                                <Button type='button' bsStyle='info' bsSize='xs' onClick={props.onToggleMetric}>
                                    {props.showMetric ? 'Convert to US' : 'Convert to Metric'}
                                </Button>
                            </Col>
                            <Col xs={12} lg={5}>
                                {props.showMetric ?
                                    <InputGroup>
                                        <FormControl
                                            name="height"
                                            maxLength={3}
                                            value={tempHeightCmText}
                                            onChange={handleCmChange}
                                            onBlur={handleCmBlur}
                                        />
                                        <InputGroup.Addon>
                                            cm
                                        </InputGroup.Addon>
                                    </InputGroup>

                                    :
                                    <InputGroup>
                                        <FormControl
                                            componentClass='select'
                                            name='heightFeet'
                                            value={tempHeightFeet}
                                            onChange={handleHeightChange}
                                        >
                                            {heightFeet.map(ft => (
                                                <option key={`ft-${ft}`} value={ft}>{ft}</option>
                                            ))}

                                        </FormControl>
                                        <InputGroup.Addon>
                                            ft
                                        </InputGroup.Addon>
                                        <FormControl
                                            componentClass='select'
                                            name={'heightInches'}
                                            value={tempHeightInches}
                                            onChange={handleHeightChange}
                                        >
                                            {heightInches.map(inch => (
                                                <option key={`in-${inch}`} value={inch}>{inch}</option>
                                            ))}
                                        </FormControl>
                                        <InputGroup.Addon>
                                            in
                                        </InputGroup.Addon>
                                    </InputGroup>
                                }
                            </Col>
                        </FormGroup>

                        <FormGroup validationState={(props.showMetric ? props.validation.weightKgs == false : props.validation.weightLbs == false) ? 'error' : null}>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Weight:&nbsp;
                                <Button type='button' bsStyle='info' bsSize='xs' onClick={props.onToggleMetric}>
                                    {props.showMetric ? 'Convert to US' : 'Convert to Metric'}
                                </Button>
                            </Col>
                            <Col xs={12} lg={5}>
                                {props.showMetric ?
                                    <InputGroup>
                                        <FormControl
                                            name='weight'
                                            maxLength={3}
                                            value={props.data.weight}
                                            onChange={handleNumberChange}
                                        />
                                        <InputGroup.Addon>
                                            kgs
                                        </InputGroup.Addon>
                                    </InputGroup>
                                    :
                                    <InputGroup>
                                        <FormControl
                                            name='weightLbs'
                                            maxLength={3}
                                            value={props.data.weightLbs}
                                            onChange={handleWeightChange}
                                        />
                                        <InputGroup.Addon>
                                            lbs
                                        </InputGroup.Addon>
                                    </InputGroup>
                                }
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Gender: </Col>
                            <Col xs={12} lg={4}>
                                <label className='radio-inline'>
                                    <input id='GenderM' name='gender' type='radio' value='M' className='gender_radio' checked={data.gender == 'M'} onClick={handleClick} />
                                    <strong>Male</strong>
                                </label>
                                <label className='radio-inline'>
                                    <input id='GenderF' name='gender' type='radio' value='F' className='gender_radio' checked={data.gender == 'F'} onClick={handleClick} />
                                    <strong>Female</strong>
                                </label>
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Race:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='race'
                                    value={data.race}
                                    onChange={handleSelectChange}
                                >
                                    <option value={''}>- Select -</option>
                                    {raceList.map(rl =>
                                        <option key={'rl-' + rl.id} value={rl.value}>{rl.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Does Recipient have Diabetes?</Col>
                            <Col xs={12} lg={4}>
                                <div className='form-control-static'>
                                    <YesNoUnknown
                                        name='hasDiabetes'
                                        yes={data.hasDiabetes == true}
                                        no={data.hasDiabetes == false}
                                        unknown={data.hasDiabetes == null}
                                        onChange={handleBooleanChange}
                                    />
                                </div>
                            </Col>
                        </FormGroup>
                    </Col>

                    <Col xs={12} lg={6}>
                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>Reason for Kidney Failure:</Col>
                            <Col xs={12} lg={8}>
                                <FormControl
                                    name='reasonForKidneyFailure'
                                    value={data.reasonForKidneyFailure}
                                    onChange={handleTextChange}
                                />
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>EBV:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='ebv'
                                    value={data.ebv}
                                    onChange={handleSelectChange}
                                >

                                    {positiveNegativeMap.map(i =>
                                        <option key={'pos1-' + i.value} value={i.value}>{i.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>CMV:</Col>
                            <Col xs={12} lg={4}>
                                <FormControl
                                    componentClass='select'
                                    name='cmv'
                                    value={data.cmv}
                                    onChange={handleSelectChange}
                                >
                                    {positiveNegativeMap.map(i =>
                                        <option key={'pos2-' + i.value} value={i.value}>{i.description}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={4} componentClass={ControlLabel}>UNOS List Date:</Col>
                            <Col xs={12} lg={4}>
                                <MaskedInput
                                    name='unosDate'
                                    className='form-control'
                                    mask='11/11/1111'
                                    placeholder='mm/dd/yyyy'
                                    value={data.unosDate}
                                    onChange={handleTextChange}
                                />
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12} lg={12}>
                        <FormGroup>
                            <Col xs={12} lg={12}>
                                <strong>Any Notable Medical Issues</strong>
                                <FormControl
                                    style={{ height: 100, resize: 'none' }}
                                    componentClass='textarea'
                                    name='medComments'
                                    value={data.medComments}
                                    onChange={handleTextChange}
                                />
                                <span style={{ fontSize: 12 }}>(500 character limit)</span>
                            </Col>
                        </FormGroup>
                    </Col>
                </Row>
            </ViewWrapper>
        </>
    );
}


export default Content;
